import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {createSelector} from 'reselect';
import type {ShopperCustomersTypes} from 'commerce-sdk-isomorphic';
import type {CustomerProductListItem, CustomerProductListState, CustomerProductListsState} from './types';
import type {RootState} from '../../types';

const customerProductListsSelector = (state: RootState) => state.customerProductLists;

export const customerProductListsSlice = createSlice({
	name: 'customerProductLists',
	initialState: {} as CustomerProductListsState,
	reducers: {
		receiveLists: (state: CustomerProductListsState, action: PayloadAction<ShopperCustomersTypes.CustomerProductListResult>) => {
			if (!action.payload.total) {
				return;
			}

			let lists = {};

			action.payload.data.forEach((productList) => {
				lists[productList.id] = productList;
			});

			state = Object.assign(state, lists);
		},
		receiveList: (state: CustomerProductListsState, action: PayloadAction<CustomerProductListState>) => {
			const {id} = action.payload;

			state[id] = action.payload;
		},
		setListVisibility: (state: CustomerProductListsState, action: PayloadAction<{listId: string; isPublic: boolean}>) => {
			const {listId, isPublic} = action.payload;

			state[listId] = state[listId] || {} as CustomerProductListState;
			state[listId].public = isPublic;
			state[listId].customerProductListItems?.forEach((item) => {
				item.public = isPublic;
			});
		},
		createListItem: (state: CustomerProductListsState, action: PayloadAction<{listId: string; item: CustomerProductListItem}>) => {
			const {listId, item} = action.payload;
			const {productId} = item;

			state[listId] = state[listId] || {} as CustomerProductListState;

			const items = state[listId].customerProductListItems || [];
			const existingItemIndex = items.findIndex((i) => i.productId === productId);

			if (existingItemIndex >= 0) {
				items[existingItemIndex] = item;
			} else {
				items.push(item);
			}

			state[listId].customerProductListItems = items;
		},
		updateListItem: (state: CustomerProductListsState, action: PayloadAction<{listId: string; item: CustomerProductListItem}>) => {
			const {listId, item} = action.payload;

			state[listId] = state[listId] || {} as CustomerProductListState;

			const items = state[listId].customerProductListItems?.map(
				(listItem) => {
					if (listItem.id === item.id) {
						return item;
					}

					return listItem;
				}
			);

			state[listId].customerProductListItems = items;
		},
		removeListItem: (state: CustomerProductListsState, action: PayloadAction<{listId: string; itemId: string}>) => {
			const {listId, itemId} = action.payload;

			state[listId] = state[listId] || {} as CustomerProductListState;
			state[listId].customerProductListItems = state[listId].customerProductListItems?.filter((listItem) => {
				return listItem.id !== itemId;
			});
		},
		resetCustomerProductLists: (state: CustomerProductListsState) => {
			for (const key in state) {
				delete state[key];
			}
		}
	}
});

export const {
	receiveLists,
	receiveList,
	setListVisibility,
	createListItem,
	updateListItem,
	removeListItem,
	resetCustomerProductLists
} = customerProductListsSlice.actions;

export const selectCustomerProductLists = createSelector(customerProductListsSelector,
	(customerProductLists) => customerProductLists);

export default customerProductListsSlice.reducer;
